import React, { useEffect, useState } from 'react';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { Table, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPen } from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const TestMarksEntryPage = () => {
  const navigate = useNavigate();
  const { testId } = useParams(); // Assuming the testId is part of the URL
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatedMarks, setUpdatedMarks] = useState({});

  const fetchTestMarks = async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-test-marks-v2?test_id=${testId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data.status === 'success') {
        const studentsData = response.data.students;
        setStudents(studentsData);
        const marksData = {};
        studentsData.forEach((student) => {
          marksData[student.student_id] = student.mark || '';
        });
        setUpdatedMarks(marksData);
      } else {
        console.error('Failed to fetch test marks:', response.data.message);
        setStudents([]);
      }
    } catch (error) {
      console.error('Error fetching test marks:', error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkChange = (studentId, mark) => {
    setUpdatedMarks((prev) => ({
      ...prev,
      [studentId]: mark,
    }));
  };

  const saveMark = async (studentId, mark) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      await axios.post(
        `${appConstants.API_ROOT_URL}save-single-test-mark-v2`,
        { test_id: testId, student_id: studentId, mark },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (error) {
      console.error(`Error saving mark for student ${studentId}:`, error);
    }
  };

  const handleInputEvent = (e, studentId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveMark(studentId, updatedMarks[studentId]);
    }
  };

  const handleBlur = (studentId) => {
    saveMark(studentId, updatedMarks[studentId]);
  };

  const handleBackClick = () => {
    navigate(-1); // Goes back to the previous page
  };

  useEffect(() => {
    fetchTestMarks();
  }, [testId]);

  return (
    <Layout>
      <div className="container mt-4">
        {/* Compact Top Bar */}
        <div
          style={{
            background: '#343a40',
            color: '#fff',
            borderRadius: 8,
            padding: '0.6rem 1rem',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <FaPen />
              Test Marks Entry
            </div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              {students.length} students
            </div>
          </div>
          <Button
            variant="outline-light"
            size="sm"
            style={{
              borderRadius: 6,
              padding: '6px 10px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={handleBackClick}
          >
            <FaArrowLeft />
          </Button>
        </div>

        {loading ? (
          <div className="text-center mt-5">
            <Spinner animation="border" style={{ color: '#343a40' }} />
            <p className="mt-2 text-muted">Loading students...</p>
          </div>
        ) : (
          <>
            {/* Modern Table */}
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                overflowX: 'auto',
              }}
            >
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead
                    style={{
                      background: '#f8f9fa',
                      borderBottom: '3px solid #dee2e6',
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          padding: '12px 20px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          width: '80px',
                          letterSpacing: '0.5px',
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          padding: '12px 20px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Student Name
                      </th>
                      <th
                        style={{
                          padding: '12px 20px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          textAlign: 'center',
                          width: '200px',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Mark
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student, index) => (
                      <tr
                        key={student.student_id}
                        style={{ borderBottom: '1px solid #f0f0f0' }}
                      >
                        <td
                          style={{
                            padding: '10px 20px',
                            verticalAlign: 'middle',
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{
                            padding: '10px 20px',
                            verticalAlign: 'middle',
                            fontWeight: 500,
                          }}
                        >
                          {student.name}
                        </td>
                        <td
                          style={{ padding: '10px 20px', textAlign: 'center' }}
                        >
                          <Form.Control
                            type="text"
                            value={updatedMarks[student.student_id] || ''}
                            onChange={(e) =>
                              handleMarkChange(
                                student.student_id,
                                e.target.value,
                              )
                            }
                            onKeyDown={(e) =>
                              handleInputEvent(e, student.student_id)
                            }
                            placeholder="Enter mark"
                            style={{
                              padding: '6px 10px',
                              borderRadius: 6,
                              border: '2px solid #e9ecef',
                              fontSize: '0.9rem',
                              textAlign: 'center',
                              maxWidth: 120,
                              margin: '0 auto',
                              transition: 'all 0.2s ease',
                              height: '36px',
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#667eea';
                              e.target.style.boxShadow =
                                '0 0 0 3px rgba(102, 126, 234, 0.1)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e9ecef';
                              e.target.style.boxShadow = 'none';
                              handleBlur(student.student_id);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            {/* Help Text */}
            <div
              className="mt-3 text-muted text-center"
              style={{ fontSize: '0.9rem' }}
            >
              <div className="mb-1">
                <strong>Quick Tips:</strong>
              </div>
              <div>
                Press Enter to save and move to next field • Use letters for
                grades (A, B, C, etc.)
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default TestMarksEntryPage;
